import React, { useEffect } from "react";
import {
  FormGroup,
  Label,
  Input,
  ErrorText,
  FormContainer,
  InputIcon,
  FullWidthContainer,
  InputField,
  NameFieldWrapper,
  NameFieldsContainer,
  FormSubTitle,
} from "../../../styles/PersonalInfoStep";

import { MapContainerWrapper } from "../../../styles/StoreLocationStep";
import {
  LoadingOverlay,
  LoadingSpinner,
  LoadingSpinnerIcon,
} from "../../../styles/loading";

import { useNavigate } from "react-router-dom";
import {
  alertMessage,
  injectCustomStyles,
} from "../../../services/alertService";
import { ALERT_TITLES, ALERT_MESSAGES } from "../../../constants";
import { ChangePostalToAddress } from "../../../services/geocodeService";
import { setItemWithObject } from "../../../utils/cache/LocalStorage";
import {
  PatternValidators,
  TextValidators,
  validPattern,
  validText,
} from "../../../utils/validators";
import Swal from "sweetalert2";
import { POST } from "../../../services/HTTPServices";
import { OnBoardingStepProps } from "../../../constants";
import MapContainer from "./molecules/MerchantSignupMap";
import Store from "../../../assets/svgs/LoginSignup/Step2/store.svg";
import Locator from "../../../assets/svgs/LoginSignup/Step2/locator.svg";
import { StepForm } from "../../../components/signup/StepForm";

const initialValues = {
  shopName: "",
  postcode: "",
  streetName: "",
  buildingName: "",
  block: "",
  floor: "",
  unit: "",
  businessType: "personal",
  hawkerCenter: "",
};

type Values = typeof initialValues;



interface StoreLocationStepProps extends Omit<OnBoardingStepProps, 'onNext'> {
  onNext: (data?: any) => void;
}

const StoreLocationStep: React.FC<StoreLocationStepProps> = ({
  formData,
  onChange,
  onNext,
  onBack,
  changedRole,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);

  const [location, setLocation] = React.useState<{
    lat: number;
    lng: number;
  } | null>(null);

  const [values, setValues] = React.useState(initialValues);

  const [errors, setErrors] = React.useState<{ [key: string]: boolean }>({});

  //validation
  const [submitBtnClick, setSubmitBtnClick] = React.useState(false);

  useEffect(() => {
    const loadInitialData = async () => {
      setSubmitBtnClick(false);
      setLoading(true);
      const tempUser = JSON.parse(localStorage.getItem("tempUser") || "null");

      if (tempUser) {
        setValues((prev) => ({
          ...prev,
          shopName: tempUser.shopName || "",
          postcode: tempUser.postcode || "",
          streetName: tempUser.street || "",
          buildingName: tempUser.building || "",
          block: tempUser.block || "",
          floor: tempUser.floor || "",
          unit: tempUser.unit || "",
        }));

        if (tempUser.postcode && tempUser.postcode.length >= 5) {
          try {
            const res = (await ChangePostalToAddress(tempUser.postcode)) as
              | { status: number; data: any }
              | undefined;

            if (res?.status === 200 && res.data) {
              setValues((prev) => ({
                ...prev,
                streetName: res.data.street || "",
                block: res.data.block || "",
                buildingName: res.data.building || "",
              }));
              setLocation(res.data.loc);
              setItemWithObject("saved_coord", {
                lat: res.data.lat,
                lng: res.data.lng,
              });
            } else {
              alertMessage(ALERT_TITLES.ERROR, ALERT_MESSAGES.GPS_ERROR);
            }
          } catch (error) {
            console.error("Error fetching address:", error);
            alertMessage(ALERT_TITLES.ERROR, ALERT_MESSAGES.GPS_ERROR);
          }
        }
      }
      setLoading(false);
    };

    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const fieldName = name as keyof Values;
    onChange(name, value);

    if (fieldName === "postcode") {
      const filteredValue = value.replace(/\D/g, ""); // Remove non-numeric characters
      if (filteredValue.length <= 6) {
        setValues((prev) => ({ ...prev, postcode: filteredValue }));
        await validateField("postcode", filteredValue);

        if (filteredValue.length >= 5) {
          try {
            const res = (await ChangePostalToAddress(filteredValue)) as
              | {
                  status: number;
                  data: {
                    street: string;
                    block: string;
                    building: string;
                    floor: string;
                    unit: string;
                    loc: { lat: number; lng: number };
                  };
                }
              | undefined;

            if (res?.status === 200 && res.data) {
              const { street, block, building, floor, unit, loc } = res.data;
              setValues((prev) => ({
                ...prev,
                streetName: street || "",
                block: block || "",
                buildingName: building || "",
                floor: floor || "",
                unit: unit || "",
              }));
              setLocation(loc);

              // Validate all the fields that were auto-filled
              await Promise.all([
                validateField("streetName", street || ""),
                validateField("block", block || ""),
                validateField("buildingName", building || ""),
                validateField("floor", floor || ""),
                validateField("unit", unit || ""),
              ]);
            } else {
              alertMessage(ALERT_TITLES.ERROR, ALERT_MESSAGES.GPS_ERROR);
            }
          } catch (error) {
            console.error("Error fetching address:", error);
            alertMessage(ALERT_TITLES.ERROR, ALERT_MESSAGES.GPS_ERROR);
          }
        } else {
          // Postcode is less than 5 chars, clear auto-filled fields
          setValues((prev) => ({
            ...prev,
            streetName: "",
            block: "",
            buildingName: "",
            floor: "",
            unit: "",
          }));
          setLocation(null);
        }
      }
    } else {
      // Handle other input fields
      setValues((prev) => ({ ...prev, [fieldName]: value }));
      await validateField(fieldName, value);
    }
  };

  const validateField = async (prop: keyof Values, value: string) => {
    let isValid: boolean = true;

    switch (prop) {
      case "shopName": {
        isValid = await validText({ text: value });
        break;
      }
      case "streetName": {
        isValid = await validText({ text: value });
        break;
      }
      case "buildingName": {
        isValid = await validText({ text: value });
        break;
      }
      case "block": {
        isValid = await validText({ text: value });
        break;
      }
      case "postcode": {
        isValid = await validPattern({
          paramPattern: value,
          patternType: "postalCode",
        });
        break;
      }
      case "floor": {
        isValid = await validPattern({
          paramPattern: value,
          patternType: "floor",
        });
        break;
      }
      case "unit": {
        isValid = await validPattern({
          paramPattern: value,
          patternType: "unit",
        });
        break;
      }
    }
    setErrors((prev) => ({
      ...prev,
      [prop]: !isValid,
    }));
    return isValid;
  };

  const back = () => {
    alertMessageWithCancel(
      ALERT_TITLES.WARNING,
      ALERT_MESSAGES.UNSAVED_CHANGES,
      "Leave"
    );
  };

  const alertMessageWithCancel = (
    title: string,
    text: string,
    confirmButtonText: string
  ) => {
    injectCustomStyles();

    const swalWithBootstrapButtons = Swal.mixin({
      customClass: {
      confirmButton: title === "Error!"? "custom-error-btn" : "custom-confirm-btn",
      cancelButton: "custom-cancel-btn",
    },
      buttonsStyling: false,
    });

    swalWithBootstrapButtons
      .fire({
        title: title,
        text: text,
        icon:
          title === "Error!"
            ? "error"
            : title === "Success!"
            ? "success"
            : "warning",
        confirmButtonText: confirmButtonText || "OK",
        showCancelButton: true,
         customClass: {
      confirmButton: title === "Error!"? "custom-error-btn" : "custom-confirm-btn",
      cancelButton: "custom-cancel-btn",
    },
      })
      .then((result: any) => {
        if (result.isConfirmed) {
          const provider_login_email =
            localStorage.getItem("provider_login_email") || "";
          if (provider_login_email) {
            const provider_login_mobile =
              localStorage.getItem("provider_login_mobile") || "";
            if (provider_login_mobile) {
              rollback(provider_login_email, provider_login_mobile);
            }
          }
        }
      });
  };

  const rollback = async (email: string, mobile: string) => {
    setLoading(true);

    let url = "serviceprovider/RollbackMerchant";

    let temp = {
      email: email,
      mobile: "+65" + mobile,
    };

    let res = (await POST(url, temp, "provider")) as
      | { status: number; message: string }
      | undefined;

    if (res?.status === 200) {
      Object.keys(localStorage).filter(k => k !== 'accountId' && k !== 'login_credentials').forEach(k => localStorage.removeItem(k));      
      setLoading(false);
      onBack("rollback");
    } else {
      setLoading(false);

      if (res?.status === 401) {
        navigate("/unauthorized-page");
      } else {
        alertMessage(
          ALERT_TITLES.ERROR,
          res?.message || "An unknown error occurred"
        );
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    setSubmitBtnClick(true);

    const fieldsToValidate: (keyof Values)[] = [
      "shopName",
      "postcode",
      "streetName",
      "block",
      "floor",
      "unit",
    ];

    const validationResults = await Promise.all(
      fieldsToValidate.map((field) => validateField(field, values[field]))
    );

    const hasErrors = validationResults.some((isValid) => !isValid);
    const isFormValid = !hasErrors;

    if (isFormValid) {
      const tempUser = JSON.parse(localStorage.getItem("tempUser") || "null");
      if (tempUser) {
        tempUser.shopName = values.shopName;
        tempUser.postcode = values.postcode;
        tempUser.street = values.streetName;
        tempUser.building = values.buildingName;
        tempUser.block = values.block;
        tempUser.floor = values.floor;
        tempUser.unit = values.unit;

        setItemWithObject("tempUser", tempUser);
        onNext();
      }
    }
  };

  return (
    <>
      {loading && (
        <LoadingOverlay>
          <LoadingSpinner>
            <LoadingSpinnerIcon />
          </LoadingSpinner>
        </LoadingOverlay>
      )}
      <FormContainer>
      <StepForm onNext={handleSubmit} onBack={back}>
      <FormSubTitle> What is your shop’s name?</FormSubTitle>

          <FormGroup $hasError={!!errors.shopName}>
            <InputIcon>
              <img src={Store} alt="Store" />
            </InputIcon>
            <FullWidthContainer>
              <Label>Shop Name*</Label>
              <InputField $hasError={!!errors.shopName}>
                <Input
                  type="text"
                  name="shopName"
                  placeholder="Enter your shop name"
                  value={values.shopName}
                  onChange={handleChange}
                  $hasError={!!errors.shopName}
                  className="ml-negative-16"
                />
              </InputField>
            </FullWidthContainer>
          </FormGroup>
          {submitBtnClick && errors.shopName && (
            <ErrorText>
              <TextValidators text={values.shopName} textType="Shop Name" />
            </ErrorText>
          )}

          <FormSubTitle $marginTop="16px">Where is your shop?</FormSubTitle>

          <FormGroup $hasError={!!errors.postcode}>
            <FullWidthContainer>
              <Label>Postal Code*</Label>
              <InputField $hasError={!!errors.postcode}>
                <Input
                  type="text"
                  name="postcode"
                  placeholder="Enter your postal code"
                  value={values.postcode}
                  onChange={handleChange}
                  $hasError={!!errors.postcode}
                  className="ml-negative-16"
                />
              </InputField>
            </FullWidthContainer>
          </FormGroup>
          {submitBtnClick && errors.postcode && (
            <ErrorText>
              <PatternValidators
                paramPattern={values.postcode}
                patternType="postalCode"
                textType="Postal Code"
              />{" "}
            </ErrorText>
          )}

          <FormGroup $hasError={!!errors.streetName}>
            <InputIcon>
              <img src={Locator} alt="Locator" />
            </InputIcon>
            <FullWidthContainer>
              <Label>Street Name*</Label>
              <InputField $hasError={!!errors.streetName}>
                <Input
                  type="text"
                  name="streetName"
                  placeholder="Enter your street name"
                  value={values.streetName}
                  onChange={handleChange}
                  $hasError={!!errors.streetName}
                  className="ml-negative-16"
                />
              </InputField>
            </FullWidthContainer>
          </FormGroup>
          {submitBtnClick && errors.streetName && (
            <ErrorText>
              <TextValidators text={values.streetName} textType="Street Name" />
            </ErrorText>
          )}

          <FormGroup $hasError={!!errors.email}>
            <InputIcon>
              <img src={Locator} alt="Locator" />
            </InputIcon>
            <FullWidthContainer>
              <Label>Building Name</Label>
              <InputField $hasError={!!errors.buildingName}>
                <Input
                  type="buildingName"
                  name="buildingName"
                  placeholder="Enter your building name"
                  value={values.buildingName}
                  onChange={handleChange}
                  $hasError={!!errors.buildingName}
                  className="ml-negative-16"
                />
              </InputField>
            </FullWidthContainer>
          </FormGroup>

          {/* First Name and Last Name Fields */}
          <NameFieldsContainer>
            <NameFieldWrapper>
              <FormGroup $marginBottom="16px" $paddingRight="0px" $hasError={!!errors.block}>
                <InputIcon>
                  <img src={Locator} alt="Locator" />
                </InputIcon>
                <FullWidthContainer>
                  <Label>Block*</Label>
                  <InputField $hasError={!!errors.block}>
                    <Input
                      width="100%"
                      type="text"
                      name="block"
                      placeholder="Block"
                      value={values.block}
                      onChange={handleChange}
                      $hasError={!!errors.block}
                      className="ml-negative-16"
                    />
                  </InputField>
                </FullWidthContainer>
              </FormGroup>
              {submitBtnClick && errors.block && (
                <ErrorText>
                  {" "}
                  <TextValidators text={values.block} textType="Block" />
                </ErrorText>
              )}
            </NameFieldWrapper>
            <NameFieldWrapper className="ml-8">
              <FormGroup $marginBottom="16px" $paddingRight="0px" $hasError={!!errors.floor}>
                <InputIcon>
                  <img src={Locator} alt="Locator" />
                </InputIcon>
                <FullWidthContainer>
                  <Label>Floor*</Label>
                  <InputField $hasError={!!errors.floor}>
                    <Input
                      width="100%"
                      type="text"
                      name="floor"
                      placeholder="Floor"
                      value={values.floor}
                      onChange={handleChange}
                      $hasError={!!errors.floor}
                      className="ml-negative-16"
                    />
                  </InputField>
                </FullWidthContainer>
              </FormGroup>
              {submitBtnClick && errors.floor && (
                <ErrorText>
                  {" "}
                  <TextValidators text={values.floor} textType="Floor" />
                </ErrorText>
              )}
            </NameFieldWrapper>
            <NameFieldWrapper className="ml-8">
              <FormGroup $marginBottom="16px" $paddingRight="0px" $hasError={!!errors.unit}>
                <InputIcon>
                  <img src={Locator} alt="Locator" />
                </InputIcon>
                <FullWidthContainer>
                  <Label>Unit*</Label>
                  <InputField $hasError={!!errors.unit}>
                    <Input
                      type="text"
                      name="unit"
                      placeholder="Unit"
                      value={values.unit}
                      onChange={handleChange}
                      $hasError={!!errors.unit}
                      className="ml-negative-16"
                      width="100%"
                    />
                  </InputField>
                </FullWidthContainer>
              </FormGroup>
              {submitBtnClick && errors.unit && (
                <ErrorText>
                  {" "}
                  <TextValidators text={values.unit} textType="Unit" />
                </ErrorText>
              )}
            </NameFieldWrapper>
          </NameFieldsContainer>

          <MapContainerWrapper>
            {location ? <MapContainer location={location} /> : <MapContainer />}
          </MapContainerWrapper>

          
        
        </StepForm>
      </FormContainer>
    </>
  );
};


export default StoreLocationStep;
